import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MachineVersionDetails.css';
import { Link, useParams } from 'react-router-dom';
import { TIMELINE_CONST } from '../../common/constant/common-constants';
import { getMachineVersionDetails } from '../../helper/MachineVersionDetailsHelper';
import { getMachineOtaLogs } from '../../helper/MachineOtaLogsHelper';
import { getSystemVersionDetails } from '../../helper/SystemVersionDetailsHelper';
import moment from 'moment';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const parseOtaTimestamp = (timestamp) => {
  if (timestamp == null || timestamp === '') return null;

  if (typeof timestamp === 'number' || /^\d+$/.test(String(timestamp).trim())) {
    const num = Number(timestamp);
    const ms = num < 1e12 ? num * 1000 : num;
    return moment.utc(ms);
  }

  return moment.utc(timestamp);
};

const getOtaTimestampMs = (timestamp) => {
  const parsed = parseOtaTimestamp(timestamp);
  return parsed?.isValid() ? parsed.valueOf() : 0;
};

const formatOtaTimestamp = (timestamp) => {
  const parsed = parseOtaTimestamp(timestamp);
  if (!parsed?.isValid()) return '—';
  return parsed.utcOffset(330).format('HH:mm:ss, Do MMM YYYY');
};

const getFullMachineNumber = (machineUDI) => {
  if (!machineUDI) return '—';
  try {
    return decodeURIComponent(String(machineUDI));
  } catch {
    return String(machineUDI);
  }
};

const getVersionSegmentCount = (version) => {
  if (!version) return 0;
  return String(version).trim().split('.').filter(Boolean).length;
};

const isTestVersion = (softwareVersion, osVersion) => (
  getVersionSegmentCount(softwareVersion) === 4 || getVersionSegmentCount(osVersion) === 4
);

const matchesProdLatest = (softwareVersion, osVersion, prodSystem, prodOs) => {
  if (!prodSystem || !prodOs) return false;
  return String(softwareVersion ?? '').trim() === prodSystem.trim()
    && String(osVersion ?? '').trim() === prodOs.trim();
};

const getUpdateVersionLabel = (softwareVersion, osVersion, prodSystem, prodOs, isMostRecent) => {
  if (isTestVersion(softwareVersion, osVersion)) {
    return { label: 'Test', kind: 'test' };
  }

  if (isMostRecent && prodSystem && prodOs && matchesProdLatest(softwareVersion, osVersion, prodSystem, prodOs)) {
    return { label: 'Latest', kind: 'latest' };
  }

  return null;
};

const getEntryKey = (entry) => (
  `${entry.systemUpdateDate}-${entry.updatedTo?.softwareVersion}-${entry.updatedTo?.osVersion}-${entry.updatedFrom?.softwareVersion}`
);

const normalizeStatus = (status) => {
  if (!status) return { label: 'Pending', kind: 'pending' };
  const normalized = String(status).toLowerCase();
  if (normalized === 'successful') return { label: 'Updated', kind: 'success' };
  if (normalized === 'pending') return { label: 'Pending', kind: 'pending' };
  if (normalized === 'rollback') return { label: 'Rollback', kind: 'rollback' };
  return { label: status, kind: 'pending' };
};

const matchesSearch = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const fields = [
    item.updatedFrom?.softwareVersion,
    item.updatedFrom?.osVersion,
    item.updatedTo?.softwareVersion,
    item.updatedTo?.osVersion,
    item.systemUpdateStatus,
    item.systemUpdateDate,
  ];
  return fields.some((field) => String(field ?? '').toLowerCase().includes(q));
};

const normalizeOtaStatus = (status) => {
  if (!status) return { label: 'Unknown', kind: 'pending' };
  const normalized = String(status).toLowerCase();
  if (normalized === 'successful' || normalized === 'success' || normalized === 'completed') {
    return { label: 'Successful', kind: 'success' };
  }
  if (normalized === 'in_progress' || normalized === 'in progress') {
    return { label: 'In Progress', kind: 'progress' };
  }
  if (normalized === 'failed' || normalized === 'failure') {
    return { label: 'Failed', kind: 'failed' };
  }
  if (normalized === 'pending') return { label: 'Pending', kind: 'pending' };
  return { label: status, kind: 'pending' };
};

const formatStageLabel = (stage) => {
  if (!stage) return '—';
  return String(stage)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const OtaStatusBadge = ({ status }) => {
  const normalized = normalizeOtaStatus(status);
  return (
    <span className={`mvh-ota-status-badge mvh-ota-status-badge--${normalized.kind}`}>
      {normalized.label}
    </span>
  );
};

const getSessionSummary = (session) => {
  const logs = Array.isArray(session?.logs) ? session.logs : [];
  const lastLog = logs[logs.length - 1];
  return {
    logCount: logs.length,
    finalStatus: lastLog?.otaStatus,
    maxProgress: logs.reduce((max, log) => Math.max(max, log?.progress ?? 0), 0),
    osVersion: lastLog?.osVersion,
    softwareVersion: lastLog?.softwareVersion,
  };
};

const OtaLogEntry = ({ log, index }) => (
  <article className="mvh-ota-log-entry">
    <div className="mvh-ota-log-entry-index">{index + 1}</div>
    <div className="mvh-ota-log-entry-body">
      <div className="mvh-ota-log-entry-head">
        <OtaStatusBadge status={log.otaStatus} />
        <span className="mvh-ota-log-stage">{formatStageLabel(log.stage)}</span>
        {log.progress != null ? (
          <span className="mvh-ota-log-progress">{log.progress}%</span>
        ) : null}
        <span className="mvh-ota-log-time">
          {formatOtaTimestamp(log.timestamp)}
        </span>
      </div>
      <p className="mvh-ota-log-message">{displayValue(log.message)}</p>
      {(log.osVersion || log.softwareVersion) ? (
        <div className="mvh-ota-log-versions">
          {log.softwareVersion ? <span>SF: {log.softwareVersion}</span> : null}
          {log.osVersion ? <span>OS: {log.osVersion}</span> : null}
        </div>
      ) : null}
      {(log.errorCode || log.reason || log.detail) ? (
        <div className="mvh-ota-log-error">
          {log.errorCode != null ? <span>Code: {log.errorCode}</span> : null}
          {log.reason ? <span>{log.reason}</span> : null}
          {log.detail ? <p className="mvh-ota-log-detail">{log.detail}</p> : null}
        </div>
      ) : null}
    </div>
  </article>
);

const OtaSessionCollapsible = ({ session, isExpanded, onToggle }) => {
  const summary = getSessionSummary(session);
  const sessionTime = formatOtaTimestamp(session.timestamp);

  return (
    <div className={`mvh-ota-session ${isExpanded ? 'mvh-ota-session--expanded' : ''}`}>
      <button
        type="button"
        className="mvh-ota-session-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="mvh-ota-session-chevron" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
        <span className="mvh-ota-session-time">{sessionTime}</span>
        <div className="mvh-ota-session-stats">
          <span>{summary.logCount} log{summary.logCount === 1 ? '' : 's'}</span>
          {summary.softwareVersion || summary.osVersion ? (
            <span>
              {summary.softwareVersion ? `SF ${summary.softwareVersion}` : ''}
              {summary.softwareVersion && summary.osVersion ? ' · ' : ''}
              {summary.osVersion ? `OS ${summary.osVersion}` : ''}
            </span>
          ) : null}
        </div>
        <OtaStatusBadge status={summary.finalStatus} />
      </button>
      {isExpanded ? (
        <div className="mvh-ota-session-body">
          {(session.logs || []).length === 0 ? (
            <p className="mvh-ota-session-empty">No logs in this session.</p>
          ) : (
            <div className="mvh-ota-log-list">
              {session.logs.map((log, index) => (
                <OtaLogEntry key={`${log.timestamp}-${log.stage}-${index}`} log={log} index={index} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const VersionLabelBadge = ({ label }) => {
  if (!label) return null;
  return (
    <span className={`mvh-version-badge mvh-version-badge--${label.kind}`}>
      {label.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const normalized = normalizeStatus(status);
  return (
    <span className={`mvh-status-badge mvh-status-badge--${normalized.kind}`}>
      {normalized.label}
    </span>
  );
};

const MachineDetails = () => {
  const { machine_sn } = useParams();
  const searchInputRef = useRef(null);
  const [dataContainer, setDataContainer] = useState([]);
  const [versionContainer, setVersionContainer] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [otaSessions, setOtaSessions] = useState([]);
  const [otaLoading, setOtaLoading] = useState(true);
  const [otaError, setOtaError] = useState('');
  const [expandedSessions, setExpandedSessions] = useState({});
  const [activeView, setActiveView] = useState('history');

  const displaySerial = getFullMachineNumber(machine_sn);
  const machineUDI = displaySerial === '—' ? machine_sn : displaySerial;

  const fetchVersionHistory = useCallback(() => {
    getMachineVersionDetails(machine_sn)
      .then((res) => setDataContainer(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error(err));
  }, [machine_sn]);

  const fetchOtaLogs = useCallback(() => {
    if (!machineUDI) return Promise.resolve();

    setOtaLoading(true);
    setOtaError('');
    setExpandedSessions({});

    return getMachineOtaLogs(machineUDI)
      .then((sessions) => {
        const sorted = [...sessions].sort((a, b) => (
          getOtaTimestampMs(b.timestamp) - getOtaTimestampMs(a.timestamp)
        ));
        setOtaSessions(sorted);
        if (sorted[0]?.sessionId) {
          setExpandedSessions({ [sorted[0].sessionId]: true });
        }
      })
      .catch((err) => {
        setOtaSessions([]);
        setOtaError(typeof err === 'string' ? err : 'Failed to load OTA logs');
      })
      .finally(() => {
        setOtaLoading(false);
      });
  }, [machineUDI]);

  const handleRefresh = useCallback(() => {
    fetchVersionHistory();
    fetchOtaLogs();
  }, [fetchVersionHistory, fetchOtaLogs]);

  useEffect(() => {
    fetchVersionHistory();

    getSystemVersionDetails()
      .then((res) => setVersionContainer(res.data || {}))
      .catch((err) => console.error(err));
  }, [fetchVersionHistory]);

  useEffect(() => {
    fetchOtaLogs();
  }, [fetchOtaLogs]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const sortedHistory = useMemo(() => (
    [...dataContainer].sort((a, b) => {
      const dateA = a.systemUpdateDate ? new Date(a.systemUpdateDate).getTime() : 0;
      const dateB = b.systemUpdateDate ? new Date(b.systemUpdateDate).getTime() : 0;
      return dateB - dateA;
    })
  ), [dataContainer]);

  const filteredHistory = useMemo(() => (
    sortedHistory.filter((item) => matchesSearch(item, searchQuery))
  ), [sortedHistory, searchQuery]);

  const prodSystemVersion = versionContainer?.prod?.[0]?.softwareVersion?.trim() || null;
  const prodOsVersion = versionContainer?.prod?.[0]?.osVersion?.trim() || null;

  const latestEntry = sortedHistory[0];
  const mostRecentKey = latestEntry ? getEntryKey(latestEntry) : null;

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  return (
    <div className="mvh-page">
      <div className="mvh-breadcrumb">
        <Link to="/ota-details/machine-details/">Admin Dashboard</Link>
        {' > '}
        <Link to="/ota-details/machine-details/">Machine</Link>
        {' > '}
        {displaySerial}
      </div>

      <div className="mvh-header">
        <div className="mvh-header-left">
          <h3 className="mvh-title">{displaySerial}</h3>
        </div>

        <div className="mvh-header-actions">
          {activeView === 'history' ? (
            <input
              ref={searchInputRef}
              type="text"
              className="mvh-search-input"
              placeholder="Search version, status..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          ) : null}
        </div>
      </div>

      <div className="mvh-view-toggle-row">
        <div className="mvh-view-toggle" role="tablist" aria-label="Machine details view">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'history'}
            className={`mvh-view-toggle-btn ${activeView === 'history' ? 'mvh-view-toggle-btn--active' : ''}`}
            onClick={() => setActiveView('history')}
          >
            Version History
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'ota'}
            className={`mvh-view-toggle-btn ${activeView === 'ota' ? 'mvh-view-toggle-btn--active' : ''}`}
            onClick={() => setActiveView('ota')}
          >
            OTA Logs
          </button>
        </div>
        <button
          type="button"
          className="mvh-ota-refresh-btn"
          onClick={handleRefresh}
          disabled={otaLoading}
          title="Refresh version history and OTA logs"
          aria-label="Refresh version history and OTA logs"
        >
          <svg className={otaLoading ? 'mvh-ota-refresh-icon mvh-ota-refresh-icon--spin' : 'mvh-ota-refresh-icon'} viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M20 12a8 8 0 1 1-2.2-5.5M20 4v6h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="mvh-content">
        {activeView === 'history' ? (
        <section className="mvh-section">
          <div className="mvh-toolbar">
            <h2 className="mvh-section-title">Version History</h2>
          </div>

          <div className="mvh-table-wrap">
            <table className="mvh-table">
              <thead>
                <tr>
                  <th rowSpan={2} className="mvh-th-index">#</th>
                  <th colSpan={2}>UPDATED FROM</th>
                  <th colSpan={2}>UPDATED TO</th>
                  <th rowSpan={2}>DATE</th>
                  <th rowSpan={2}>TIME</th>
                  <th rowSpan={2}>STATUS</th>
                </tr>
                <tr>
                  <th>SYSTEM</th>
                  <th>OS</th>
                  <th>SYSTEM</th>
                  <th>OS</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="mvh-empty">No version history found.</td>
                  </tr>
                ) : (
                  filteredHistory.map((entry, index) => {
                    const rowIndex = index + 1;
                    const isMostRecent = mostRecentKey != null && getEntryKey(entry) === mostRecentKey;
                    const versionLabel = getUpdateVersionLabel(
                      entry.updatedTo?.softwareVersion,
                      entry.updatedTo?.osVersion,
                      prodSystemVersion,
                      prodOsVersion,
                      isMostRecent
                    );

                    return (
                      <tr
                        key={`${entry.systemUpdateDate}-${entry.updatedTo?.softwareVersion}-${rowIndex}`}
                        className={versionLabel?.kind === 'latest' ? 'mvh-row--latest' : undefined}
                      >
                        <td className="mvh-td-index">
                          {rowIndex}
                          <VersionLabelBadge label={versionLabel} />
                        </td>
                        <td>{displayValue(entry.updatedFrom?.softwareVersion)}</td>
                        <td>{displayValue(entry.updatedFrom?.osVersion)}</td>
                        <td className="mvh-td-emphasis">{displayValue(entry.updatedTo?.softwareVersion)}</td>
                        <td className="mvh-td-emphasis">{displayValue(entry.updatedTo?.osVersion)}</td>
                        <td>
                          {entry.systemUpdateDate
                            ? moment(entry.systemUpdateDate).format(TIMELINE_CONST.DATE_FORMAT)
                            : '—'}
                        </td>
                        <td>
                          {entry.systemUpdateDate
                            ? moment(entry.systemUpdateDate).format(TIMELINE_CONST.TIME_FORMAT)
                            : '—'}
                        </td>
                        <td>
                          <StatusBadge status={entry.systemUpdateStatus} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
        ) : (
        <section className="mvh-section mvh-section--ota">
          <div className="mvh-toolbar">
            <h2 className="mvh-section-title">OTA Logs</h2>
            <span className="mvh-ota-session-count">
              {otaLoading ? 'Loading...' : `${otaSessions.length} session${otaSessions.length === 1 ? '' : 's'}`}
            </span>
          </div>

          <div className="mvh-ota-logs-wrap">
            {otaLoading ? (
              <div className="mvh-ota-state">Loading OTA logs...</div>
            ) : otaError ? (
              <div className="mvh-ota-state mvh-ota-state--error">{otaError}</div>
            ) : otaSessions.length === 0 ? (
              <div className="mvh-ota-state">No OTA logs found for this machine.</div>
            ) : (
              otaSessions.map((session, index) => (
                <OtaSessionCollapsible
                  key={session.sessionId || `ota-session-${index}`}
                  session={session}
                  isExpanded={Boolean(expandedSessions[session.sessionId])}
                  onToggle={() => toggleSession(session.sessionId)}
                />
              ))
            )}
          </div>
        </section>
        )}
      </div>
    </div>
  );
};

export default MachineDetails;
